import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreatePipeline } from '../hooks/use-pipelines';
import { PipelineNode } from '../types/pipeline';
import { toast } from 'sonner';
// AIPipelineDialog replaced by full-page AI generator at /pipelines/ai-generate

interface CreatePipelineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const templates = [
  { id: 'blank', name: 'Blank Pipeline', description: 'Start from scratch' },
  { id: 'ai-generate', name: 'AI Generate', description: 'Describe what you want, AI will build it' },
  { id: 'api-warehouse', name: 'API to Warehouse', description: 'Extract from API, transform, load to warehouse' },
  { id: 'database-sync', name: 'Database Sync', description: 'Synchronize data between databases' },
  { id: 'executive-dashboard', name: 'Executive Dashboard', description: 'Multi-tier pipeline for dashboard (Hot/Warm/Cold stores)' },
];

export function CreatePipelineDialog({ open, onOpenChange }: CreatePipelineDialogProps) {
  const navigate = useNavigate();
  const createPipeline = useCreatePipeline();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('blank');
  const isPending = createPipeline.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Pipeline name is required');
      return;
    }

    try {
      const result = await createPipeline.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        nodes: getTemplateNodes(templateId),
        edges: getTemplateEdges(templateId),
      });

      onOpenChange(false);
      resetForm();

      // Navigate to the builder with the new pipeline
      navigate(`/pipelines/${result.id}`);
      toast.success('Pipeline created successfully');
    } catch {
      // Error is handled by the mutation hook
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setTemplateId('blank');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen || !isPending) {
      onOpenChange(newOpen);
      if (!newOpen) {
        resetForm();
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Create New Pipeline</DialogTitle>
              <DialogDescription>
                Create a new ETL pipeline with our visual builder. Choose a template to get started quickly.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* AI Generate Option */}
              <div className="flex items-center justify-between p-4 rounded-lg border bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950 dark:to-blue-950">
                <div className="flex-1">
                  <h4 className="text-sm font-medium">✨ AI-Powered Generation</h4>
                  <p className="text-xs text-muted-foreground">
                    Describe your pipeline in natural language and let AI build it for you
                  </p>
                </div>
                <Button
                  type="button"
                  variant="default"
                  onClick={() => {
                    onOpenChange(false);
                    navigate('/pipelines/ai-generate');
                  }}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                >
                  Generate with AI
                </Button>
              </div>

              {/* Pipeline Name */}
              <div className="space-y-2">
                <Label htmlFor="pipeline-name">
                  Pipeline Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="pipeline-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Sales Data ETL"
                  disabled={isPending}
                  autoFocus
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="pipeline-description">Description (optional)</Label>
                <Textarea
                  id="pipeline-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this pipeline does..."
                  rows={3}
                  disabled={isPending}
                />
              </div>

              {/* Template Selection */}
              <div className="space-y-2">
                <Label htmlFor="pipeline-template">Starting Template (optional)</Label>
                <Select value={templateId} onValueChange={setTemplateId} disabled={isPending}>
                  <SelectTrigger id="pipeline-template">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{template.name}</span>
                          <span className="text-xs text-muted-foreground">{template.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !name.trim()}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create & Edit
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Template helpers
function getTemplateNodes(templateId: string): PipelineNode[] {
  switch (templateId) {
    case 'api-warehouse':
      return [
        {
          id: 'source-1',
          type: 'SOURCE',
          position: { x: 80, y: 100 },
          data: { label: 'API Source', type: 'SOURCE', description: 'Extract from API' },
        },
        {
          id: 'transform-1',
          type: 'TRANSFORMATION',
          position: { x: 360, y: 120 },
          data: { label: 'Transform', type: 'TRANSFORMATION', description: 'Clean & normalize' },
        },
        {
          id: 'destination-1',
          type: 'DESTINATION',
          position: { x: 640, y: 140 },
          data: { label: 'Warehouse', type: 'DESTINATION', description: 'Load to warehouse' },
        },
      ];
    case 'database-sync':
      return [
        {
          id: 'source-1',
          type: 'SOURCE',
          position: { x: 80, y: 100 },
          data: { label: 'Source DB', type: 'SOURCE', description: 'Extract from database' },
        },
        {
          id: 'destination-1',
          type: 'DESTINATION',
          position: { x: 360, y: 100 },
          data: { label: 'Target DB', type: 'DESTINATION', description: 'Load to database' },
        },
      ];
    case 'executive-dashboard':
      return [
        {
          id: 'source-transactional',
          type: 'SOURCE',
          position: { x: 80, y: 150 },
          data: {
            label: 'Transactional Data',
            type: 'SOURCE',
            description: 'Extract from operational database',
            config: {
              sourceType: 'database',
              incrementalKey: 'updated_at',
            },
          },
        },
        {
          id: 'source-external-api',
          type: 'SOURCE',
          position: { x: 80, y: 300 },
          data: {
            label: 'External API',
            type: 'SOURCE',
            description: 'Fetch from external services',
            config: {
              sourceType: 'api',
              schedule: '*/5 * * * *',
            },
          },
        },
        {
          id: 'transform-enrich',
          type: 'TRANSFORMATION',
          position: { x: 350, y: 225 },
          data: {
            label: 'Normalize & Enrich',
            type: 'TRANSFORMATION',
            description: 'Join dimensions, calculate metrics',
            config: {
              transformations: [
                { type: 'lookup_dimension', dimension: 'dim_date' },
                { type: 'lookup_dimension', dimension: 'dim_region' },
                { type: 'calculate_field', expression: 'net_amount - cost_amount', as: 'profit_amount' },
              ],
            },
          },
        },
        {
          id: 'split-tier',
          type: 'FILTER',
          position: { x: 620, y: 225 },
          data: {
            label: 'Split by Priority',
            type: 'FILTER',
            description: 'Route to Hot/Warm/Cold based on rules',
            config: {
              splitType: 'multi',
              rules: [
                {
                  name: 'critical',
                  condition: 'amount > 1000000 OR priority = "high"',
                  target: 'hot-store',
                },
                {
                  name: 'standard',
                  condition: 'amount > 0',
                  target: 'warm-store',
                },
                {
                  name: 'archive',
                  condition: 'true',
                  target: 'cold-store',
                },
              ],
            },
          },
        },
        {
          id: 'hot-store',
          type: 'DESTINATION',
          position: { x: 900, y: 50 },
          data: {
            label: 'HOT Store (Redis)',
            type: 'DESTINATION',
            description: 'Real-time KPI, TTL 5-15 min',
            config: {
              destinationType: 'redis',
              keyPattern: 'exec:{metric}:{timeRange}',
              ttl: 300,
              aggregations: [
                { name: 'today_revenue', type: 'sum', field: 'net_amount', window: '1d' },
                { name: 'order_count', type: 'count', window: '1d' },
              ],
            },
          },
        },
        {
          id: 'warm-store',
          type: 'DESTINATION',
          position: { x: 900, y: 200 },
          data: {
            label: 'WARM Store (PostgreSQL)',
            type: 'DESTINATION',
            description: 'Operational data with indexes',
            config: {
              destinationType: 'database',
              table: 'fact_sales_transactions',
              mode: 'upsert',
              key: 'transaction_id',
            },
          },
        },
        {
          id: 'cold-store',
          type: 'DESTINATION',
          position: { x: 900, y: 350 },
          data: {
            label: 'COLD Store (ClickHouse)',
            type: 'DESTINATION',
            description: 'Historical archive, full analytics',
            config: {
              destinationType: 'clickhouse',
              table: 'fact_sales_transactions_all',
              mode: 'append',
              partitionBy: 'toYYYYMM(transaction_time)',
            },
          },
        },
      ];
    case 'ai-generate':
      return [];
    default:
      return [
        {
          id: 'source-1',
          type: 'SOURCE',
          position: { x: 80, y: 100 },
          data: { label: 'Data Source', type: 'SOURCE', description: 'Extract data' },
        },
        {
          id: 'destination-1',
          type: 'DESTINATION',
          position: { x: 360, y: 100 },
          data: { label: 'Destination', type: 'DESTINATION', description: 'Load data' },
        },
      ];
  }
}

function getTemplateEdges(templateId: string) {
  switch (templateId) {
    case 'api-warehouse':
      return [
        { id: 'e1-2', source: 'source-1', target: 'transform-1' },
        { id: 'e2-3', source: 'transform-1', target: 'destination-1' },
      ];
    case 'database-sync':
      return [{ id: 'e1-2', source: 'source-1', target: 'destination-1' }];
    case 'ai-generate':
      return [];
    case 'executive-dashboard':
      return [
        { id: 'e1-2', source: 'source-transactional', target: 'transform-enrich' },
        { id: 'e2-3', source: 'source-external-api', target: 'transform-enrich' },
        { id: 'e3-4', source: 'transform-enrich', target: 'split-tier' },
        { id: 'e4-hot', source: 'split-tier', target: 'hot-store', label: 'critical' },
        { id: 'e4-warm', source: 'split-tier', target: 'warm-store', label: 'standard' },
        { id: 'e4-cold', source: 'split-tier', target: 'cold-store', label: 'archive' },
      ];
    default:
      return [{ id: 'e1-2', source: 'source-1', target: 'destination-1' }];
  }
}
